import { ScrollView, ActivityIndicator, StyleSheet, Platform, Image } from 'react-native'
import ScreenWrapper from '@app/hoc/ScreenWrapper'
import { Theme, ThemedText, ThemedView } from '@app/theme'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import CommonHeader from '@app/sharedComponents/Header/Header'
import { PrivateStackParamList } from '@app/navigationApp/types'
import Stepper from '@app/sharedComponents/Stepper/Stepper'
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import Button from '@app/sharedComponents/Button/Button'
import { useTheme } from '@shopify/restyle'
import { fetchInitV2Api, getCredentialsForAssetFetchApi, getPledgeDataApi, triggerOtp } from './assetPledgeService'
import LinearProgressBar from '@app/sharedComponents/LinearProgressBar'
import SystemError from '../../../assets/Icons/SystemErrorIcon'
import PledgePoweredBy from './Components/PledgePoweredBy'
import { addCommasToNumber, trackEvent } from '@app/utils'
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet'
import { showErrorSheet } from '@app/store/slices/errorSlice'
import KarvyPledgeOtpModal from './Components/KarvyPledgeOtpModal'
import { getNextRoute } from '@app/navigationApp/navigationUtil'
import { useStepperData } from '@app/hooks/useStepperData'
import { createShimmerPlaceholder } from 'react-native-shimmer-placeholder'
import LinearGradient from 'react-native-linear-gradient'
import { useIsEnhanceLimit } from '@app/hooks/useIsEnhanceLimit'
import { useIsFocused } from '@react-navigation/native'
import handleApiErr from '@app/helper/handleApiErr'
import { updatePartnerParams } from '@app/store/slices/platformSlice'
import MFC_LOTTIE from '../../assets/lotties/MFcentral.json'

const ShimmerPlaceholder = createShimmerPlaceholder(LinearGradient);


export const PLEDGE_VENDOR_TYPE = {
    CAMS_PLEDGE: 'CAMS_PLEDGE',
    KFIN_PLEDGE: 'KFIN_PLEDGE',
    KFIN_FETCH: 'KFIN_FETCH',
    CAMS_FETCH: 'CAMS_FETCH',
} as const;

export const ASSET_REPOSITORY = {
    CAMS: 'CAMS',
    KARVY: 'KARVY',
    MFC: 'MFC',
} as const;

export type PledgeVendorType = keyof typeof PLEDGE_VENDOR_TYPE;


type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'ASSET_PLEDGE'>

const KFIN_PLEDGE_LOTTIE_URL = 'https://lottie.host/07df683d-9f8f-409b-8573-cf62029c3d36/FDlfEbnATZ.json'
const CAMS_PLEDGE_LOTTIE_URL = 'https://lottie.host/f1f4dbfe-6779-4aaa-9bfb-565ea73054a6/fd85MvxBAK.json'
const MFC_PLEDGE_LOTTIE_URL = MFC_LOTTIE;

const AssetPledge = ({ navigation, route }: ScreenProps) => {

    const userDetails = useAppSelector(state => state.auth.userDetails);
    const { colors } = useTheme<Theme>();
    const dispatch = useAppDispatch();

    const { stepperData, isLoading: isStepperLoading } = useStepperData()
    const isFocused = useIsFocused()


    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [retryErrorCount,setRetryErrorCount] = useState<number>(0)
    const [pledgeData, setPledgeData] = useState<any | null>(null)
    const [isOTPLoading, setIsOTPLoading] = useState<boolean>(false)
    const [otpData, setOtpData] = useState<any | null>(null)
    const [showLoadingLottie, setShowLoadingLottie] = useState<any | null>(null)
    const [lottieCams, setLottieCams] = useState(undefined)
    const [lottieKfin, setLottieKfin] = useState(undefined)

    const [modalState, setModalState] = useState<PledgeVendorType | null>(null)

    const { isFromEnhanceLimit : isEnhanceLimit} = useIsEnhanceLimit()

    const isModalVisible = modalState === PLEDGE_VENDOR_TYPE.CAMS_PLEDGE

    const closeModal = async() => {
        setModalState(null)
        setIsLoading(true)
        await getPledgeData().finally(() => {
            setIsLoading(false);
        })
    }





    const stepResponseObject = pledgeData?.stepResponseObject;
    const numberOfOTPs = stepResponseObject?.pendingOTPMap ? Object.keys(stepResponseObject?.pendingOTPMap)?.length : 1;

    const elementAtZeroPosition = stepResponseObject?.pendingOTPMap ? Object.keys(stepResponseObject?.pendingOTPMap).find(key => stepResponseObject?.pendingOTPMap[key] === 0) : null;
    const creditLimit = (stepResponseObject?.requestCreditLimit + stepResponseObject?.approvedCreditLimit);
    const completionPercentage = stepResponseObject?.approvedCreditLimit > 0 ? (stepResponseObject?.approvedCreditLimit / (stepResponseObject?.requestCreditLimit + stepResponseObject?.approvedCreditLimit)) * 100 : 0

    const [loadingLottieKey, setLoadingLottieKey] = useState<string>('')

    useEffect(() => {
        setLoadingLottieKey(elementAtZeroPosition || '')
    }, [elementAtZeroPosition])

        useEffect(() => {
          dispatch(updatePartnerParams({showCall:true}))
        }, [])



    const getPriority = (key: string) => {
        return stepResponseObject?.pendingOTPMap && stepResponseObject?.pendingOTPMap[key] !== undefined ? stepResponseObject?.pendingOTPMap?.[key] : Number.MAX_SAFE_INTEGER;
    }

    const camsPledgePriority = getPriority(PLEDGE_VENDOR_TYPE.CAMS_PLEDGE);
    const kfinPledgePriority = getPriority(PLEDGE_VENDOR_TYPE.KFIN_PLEDGE);

    const isCamsPledgeHigherPriority = camsPledgePriority < kfinPledgePriority;
    const hasKfinNext = stepResponseObject?.pledgingOrder ? stepResponseObject?.pledgingOrder.hasOwnProperty(PLEDGE_VENDOR_TYPE.KFIN_PLEDGE) : false;


    const assetRepository = elementAtZeroPosition?.includes(ASSET_REPOSITORY.CAMS) ? ASSET_REPOSITORY.CAMS : ASSET_REPOSITORY.KARVY;


    const onResendOTP = ({ otpType, assetRepositoryTemp, isRefetchNeededTemp }: { otpType: string, assetRepositoryTemp: string, isRefetchNeededTemp: boolean }) => {
        if(assetRepositoryTemp?.includes(ASSET_REPOSITORY.KARVY)){
            trackEvent('ASSET_PLEDGE_KFIN_OTP_RESEND_CLICKED')
        } else {
            trackEvent('ASSET_PLEDGE_CAMS_OTP_RESEND_CLICKED')
        }
        if (isRefetchNeeded && elementAtZeroPosition && isRefetchNeededTemp) {
            fetchInitV2Api({
                applicationId: userDetails?.linkedApplications[0]?.applicationId,
                assetRepository: assetRepositoryTemp,
                emailId: userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail,
                panNumber: userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN,
                phoneNumber: userDetails?.linkedBorrowerAccounts[0]?.accountHolderPhoneNumber
            }).then((response) => {
                setOtpData(response?.stepResponseObject);
            })
        } else {
            triggerOtp({ applicationId: userDetails?.linkedApplications[0]?.applicationId, otpType: otpType }).then((response) => {
                setOtpData(response?.stepResponseObject);
            })
        }


    }



    const getPledgeData = async () => {
        const response = await getPledgeDataApi({ applicationId: userDetails?.linkedApplications[0]?.applicationId });
        setPledgeData(response);
    }
    const isRefetchNeeded = elementAtZeroPosition?.includes('_FETCH')



    const handleConfirmAndGetOTP = async () => {

        if(retryErrorCount >= 3) {
            trackEvent('MF_PLEDGE_PORTFOLIO_MAX_RETRY_LIMIT_REACHED', {source : assetRepository })

            setRetryErrorCount(0)
            dispatch(
                showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: 'We are facing some issue with our technical systems, please try again later.',
                    closeOnBackdropPress: true,
                    errorHeading : "Service temporarily unavailable",
                    errorType: 'MAX_RETRY_LIMIT_REACHED',
                    routeName: route?.name,
                    ctaLabel: 'Okay'
                }),
            );
            return;
        }
            
        
        setIsOTPLoading(true);
         
        if (elementAtZeroPosition) {
            if (isRefetchNeeded) {
                const userCredential = await getCredentialsForAssetFetchApi({ applicationId: userDetails?.linkedApplications[0]?.applicationId, assetRepository: assetRepository })
                fetchInitV2Api({
                    applicationId: userDetails?.linkedApplications[0]?.applicationId,
                    assetRepository: assetRepository,
                    emailId: userCredential?.emailId ?? userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail,
                    panNumber: userCredential?.panNumber ?? userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN,
                    phoneNumber: userCredential?.phoneNumber ?? userDetails?.linkedBorrowerAccounts[0]?.accountHolderPhoneNumber
                }).then((response) => {
                    setRetryErrorCount(0)
                    setOtpData(response?.stepResponseObject);
                    setModalState(PLEDGE_VENDOR_TYPE.CAMS_PLEDGE);
                }).catch((err) => {
                    setModalState(null)
                    trackEvent('ASSET_PLEDGE_ERROR_PAGE_VIEWED');
                    if(handleApiErr(err)){
                        setRetryErrorCount(retryErrorCount + 1)
                        dispatch(
                            showErrorSheet({
                                isErrorSheetVisible: true,
                                errorMessage: err?.data?.message,
                                closeOnBackdropPress: true,
                                errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                                routeName: route?.name,
                                ctaLabel: 'Okay'
                            }),
                        );
                    }
                }).finally(() => {
                    setIsOTPLoading(false);
                })

            } else {
                if(loadingLottieKey?.includes(ASSET_REPOSITORY.MFC)){
                    trackEvent('ASSET_PLEDGE_MFC_PLEDGE_OTP_PAGE_VIEW')
                } else if(loadingLottieKey?.includes(ASSET_REPOSITORY.CAMS)){
                    trackEvent('ASSET_PLEDGE_CAMS_PLEDGE_OTP_PAGE_VIEW')
                } else {
                    trackEvent('ASSET_PLEDGE_KFIN_PLEDGE_OTP_PAGE_VIEW')
                }
                triggerOtp({ applicationId: userDetails?.linkedApplications[0]?.applicationId, otpType: elementAtZeroPosition }).then((response) => {
                    setRetryErrorCount(0)
                    setOtpData(response?.stepResponseObject);
                    setModalState(PLEDGE_VENDOR_TYPE.CAMS_PLEDGE);
                }).catch((err) => {
                    setModalState(null);
                    trackEvent('ASSET_PLEDGE_ERROR_PAGE_VIEWED', {source : assetRepository});
                    if(err?.status === 500) {
                        dispatch(
                            showErrorSheet({
                              isErrorSheetVisible: true,
                              errorMessage: 'Something went wrong. Please try again later.',
                              closeOnBackdropPress: false,
                              errorType: 'SERVER_ERROR',
                              errorHeading: 'Verification failed!',
                              btnName:'Ok, got it!'
                            })
                        )
                    } else if (handleApiErr(err)) {
                        setRetryErrorCount(retryErrorCount + 1)
                        dispatch(
                            showErrorSheet({
                                isErrorSheetVisible: true,
                                errorMessage: err?.data?.message,
                                closeOnBackdropPress: true,
                                errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                                routeName: route?.name,
                                ctaLabel: 'Okay'
                            }),
                        );
                    }
                }).finally(() => {
                    getPledgeData();
                    setIsOTPLoading(false);
                });
            }
        }
    }

    const loadingLottie = useMemo(() => {
        if(loadingLottieKey && loadingLottieKey.includes(ASSET_REPOSITORY.CAMS)){
            return CAMS_PLEDGE_LOTTIE_URL
        } else if(loadingLottieKey && loadingLottieKey.includes(ASSET_REPOSITORY.KARVY)){
            return KFIN_PLEDGE_LOTTIE_URL
        } else if(loadingLottieKey && loadingLottieKey.includes(ASSET_REPOSITORY.MFC)){
            return MFC_PLEDGE_LOTTIE_URL
        } else if(loadingLottieKey && loadingLottieKey.includes('KFIN')){
            return KFIN_PLEDGE_LOTTIE_URL
        }
    }, [loadingLottieKey])


    const fetchShowLoadingLottie = async () => {
        try {
            if (!loadingLottie) {
                return;
            }
            if (typeof loadingLottie === 'string') {
                await fetch(loadingLottie, {
                    method: "GET",
                })
                    .then((response) => response.json())
                    .then((responseData) => {
                        setShowLoadingLottie(responseData);
                    })
                    .catch((error) => {
                        console.error(error);
                    })
            } else {
                setShowLoadingLottie(loadingLottie);
            }
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const latestPendingOtpMap = pledgeData?.stepResponseObject?.pendingOTPMap;
        const promises: Promise<any>[] = [];
        if(latestPendingOtpMap?.CAMS_FETCH !== undefined || latestPendingOtpMap?.CAMS_PLEDGE) {
            promises.push(fetchShowCamsLottie());
        }
        if(latestPendingOtpMap?.KFIN_FETCH !== undefined || latestPendingOtpMap?.KFIN_PLEDGE) {
            promises.push(fetchShowKarvyLottie());
        }
        Promise.all(promises).finally(() => {
            fetchShowCamsLottie(),
            fetchShowKarvyLottie()
        })
    }, [])

    const fetchShowKarvyLottie = async () => {
        try {
            await fetch(KFIN_PLEDGE_LOTTIE_URL, {
                method: "GET",
            })
                .then((response) => response.json())
                .then((responseData) => {
                    setLottieKfin(responseData);
                })
                .catch((error) => {
                    console.error(error);
                })
        } catch (error) {
            console.error(error);
        }
    } 

    const fetchShowCamsLottie = async () => {
        try {
            await fetch(CAMS_PLEDGE_LOTTIE_URL, {
                method: "GET",
            })
                .then((response) => response.json())
                .then((responseData) => {
                    setLottieCams(responseData);
                })
                .catch((error) => {
                    console.error(error);
                })
        } catch (error) {
            console.error(error);
        }
    }

    const onCompleteAllOTPs = () => {
        //Todo make 0th index kyc stepper
        navigation.reset({
            index: 0,
            routes: [{ name: 'KYC_STEPPER' }, { name: getNextRoute(userDetails?.linkedApplications[0].currentStepId, userDetails?.linkedApplications[0].applicationState, userDetails?.linkedApplications[0].stepStatusMap) }]
        })
    }

    useEffect(() => {
        setIsLoading(true);
        if(isFocused) {
            getPledgeData().finally(() => {
                setIsLoading(false);
            })
        }
    }, [isFocused])

    useEffect(() => {
        fetchShowLoadingLottie()
    }, [loadingLottieKey])

    const allLoading = isLoading || isStepperLoading

    return (
        <ScreenWrapper>
            <ThemedView flex={1} bg='bg-surface-primary'>
                <CommonHeader label='Pledge Portfolio' navigation={navigation} hideBottomBorder pageName={route.name} />
                 
                 {<Stepper isLoading={allLoading} stepperData={stepperData} />}
                <ScrollView>
                    <ThemedView padding='lg' paddingTop='2xl'>

                        <ThemedText variant='h5s' color='text-primary' mr='lg' mb='lg'>
                            {isEnhanceLimit ? 'Unlock your additional credit limit by pledging your mutual funds' : 'Unlock your credit limit by pledging your mutual funds'}
                        </ThemedText>

                        {/* limit details card */}

                        {allLoading ? (<>
                            <ShimmerPlaceholder style={{ width: '100%', height: 144, opacity: 0.5, borderRadius: 12 }} />
                        </>) : (
                            <ThemedView style={styles.cardShadow} paddingHorizontal='lg' paddingVertical='xl' borderWidth={1} borderColor='border-secondary' borderRadius='md'>
                                <ThemedView flexDirection='row' alignItems='center'>
                                    <Image
                                        source={{
                                            uri: 'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/pledge_icon.png'
                                        }}
                                        style={{ width: 52, height: 52 }}
                                    />
                                    <ThemedView ml='lg'>
                                        <ThemedText variant='b3r' mb='xs' color='text-secondary'>
                                            Credit Limit
                                        </ThemedText>
                                        <ThemedText variant='h2b' color='text-primary'>
                                            <ThemedText fontFamily='Inter' variant='h2b' color='text-primary'>₹</ThemedText>
                                            {addCommasToNumber(creditLimit)}
                                        </ThemedText>
                                    </ThemedView>
                                </ThemedView>
                                <ThemedText mt='lg' variant='b5r' mb='xs' color='text-secondary'>{Math.round(completionPercentage)}% unlocked</ThemedText>
                                <LinearProgressBar progress={completionPercentage} />
                            </ThemedView>
                        )}
                        {/* limit details end */}

                    </ThemedView>
                </ScrollView>
                {(!isModalVisible || Platform.OS !== 'web') && (
                <ThemedView
                    bg='bg-surface-primary'
                    padding='lg'
                    borderTopWidth={1}
                    borderTopColor='border-primary'
                >
                    {allLoading ? (
                        <ShimmerPlaceholder style={{ width: '100%', height: 26, opacity: 0.5, borderRadius: 4 }} />
                    ) : (
                        <ThemedView borderRadius='xs' paddingHorizontal='sm' paddingVertical='xs' bg='bg-surface-emphasis-secondary' mb='xs' flexDirection='row' alignItems='center'>
                            <SystemError color={colors['icon-emphasis-secondary']} size={16} />
                            <ThemedText textAlign='left' ml='sm' color='text-primary' variant='b4r'>
                                We will trigger {numberOfOTPs} {numberOfOTPs > 1 ? 'OTPs' : 'OTP'} to unlock your funds
                            </ThemedText>
                        </ThemedView>
                    )}
                    
                    <ThemedView marginVertical='md'>
                        <Button
                            eventName='ASSET_PLEDGE_CONFIRM_AND_GET_OTP'
                            additionalProperties={{
                                numberOfOTPs: numberOfOTPs,
                                assetRepository: assetRepository
                            }}
                            label="Confirm & get OTP"
                            type="SOLID"
                            size='MD'
                            buttonLoader={isOTPLoading}
                            handlePress={handleConfirmAndGetOTP}
                            bg={colors['bg-action-primary']}
                            textColor={colors['text-primary']}
                        />
                    </ThemedView>
                    <PledgePoweredBy />
                </ThemedView>
                )}

            </ThemedView>
            <BottomSheetContainer
                closeOnBackdropPress={false}
                showCloseIcon={false}
                isVisible={isModalVisible}
                close={() => setModalState(null)}
                onClose={() => setModalState(null)}
                dragComponent={<ThemedView style={{ width: 40, height: 4, backgroundColor: colors['border-primary'], borderRadius: 10 }} />}
                containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 }}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
            >

                <KarvyPledgeOtpModal
                    isRefetchNeeded={isRefetchNeeded}
                    otpType={elementAtZeroPosition!}
                    noOfDigits={otpData?.noOfDigits}
                    identifier={otpData?.identifier}
                    otpChannel={otpData?.otpChannel}
                    assetRepository={assetRepository}
                    onResendOTP={onResendOTP}
                    closeModal={closeModal}
                    pledgingOrder={stepResponseObject?.pendingOTPMap}
                    isCamsPledgeHigherPriority={isCamsPledgeHigherPriority}
                    onCompleteAllOTPs={onCompleteAllOTPs}
                    showLoadingLottie={showLoadingLottie}
                    setLoadingLottieKey={setLoadingLottieKey}
                    hasKfinNext={hasKfinNext} 
                    updatePledgeData={getPledgeData}    
                    pledgeData={pledgeData}
                    lottieCams={lottieCams}
                    lottieKfin={lottieKfin}
                />
            </BottomSheetContainer>
        </ScreenWrapper>
    )
}

export default AssetPledge

const styles = StyleSheet.create({

    cardShadow: {
        shadowColor: "#98A2B3",
        backgroundColor: 'white',
        shadowOffset: {
            width: 0,
            height: Platform.OS === "ios" ? 2 : 6,
        },
        shadowRadius: 20,
        shadowOpacity: Platform.OS !== "android" ? 0.2 : 1,
    }
})

